import React, { useContext } from "react";
import {
  Box,
  Button,
  Dialog,
  Link as MuiLink,
  Stack,
  Typography
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  ONBOARDING_DIALOG_HEADER_BG,
  ONBOARDING_DIALOG_HEADER_BORDER,
  GUIDANCE_CALLOUT_PANEL_BG,
  GUIDANCE_CALLOUT_PANEL_BORDER,
  KivuI18nContext,
  localizedPath,
  PAGE_PORTAL_INVENTORY_CATEGORIES,
  PAGE_ROUTE_ADD_CATEGORY_SOP,
  PAGE_ROUTE_LEARN_MORE_CATEGORIES,
  PORTAL_PREFIX,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";

interface NoCategoryForProductDialogProps {
  open: boolean;
  onClose: () => void;
}

const HIGHLIGHTS = [
  {
    Icon: LayersOutlinedIcon,
    titleKey: "no-category-for-product-highlight-organize-title",
    titleFallback: "Keep your catalog tidy",
    descKey: "no-category-for-product-highlight-organize-desc",
    descFallback:
      "Every product belongs to a category so similar items stay grouped together."
  },
  {
    Icon: InsightsOutlinedIcon,
    titleKey: "no-category-for-product-highlight-find-title",
    titleFallback: "Find products faster",
    descKey: "no-category-for-product-highlight-find-desc",
    descFallback:
      "Categories make selling and reviewing reports quicker and clearer."
  },
  {
    Icon: Inventory2OutlinedIcon,
    titleKey: "no-category-for-product-highlight-products-title",
    titleFallback: "Then add your products",
    descKey: "no-category-for-product-highlight-products-desc",
    descFallback:
      "Please add at least one category, then return here to create your product."
  }
] as const;

/**
 * Explains that categories are required before adding products and links to the add-category run book.
 */
const NoCategoryForProductDialog: React.FC<NoCategoryForProductDialogProps> = ({
  open,
  onClose
}) => {
  const theme = useTheme();
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const addCategorySopPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_ADD_CATEGORY_SOP, language, defaultLang)
      : `/${PAGE_ROUTE_ADD_CATEGORY_SOP}`;

  const categoriesLearnMorePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_LEARN_MORE_CATEGORIES, language, defaultLang)
      : `/${PAGE_ROUTE_LEARN_MORE_CATEGORIES}`;

  const categoriesPortalPath =
    language != null && defaultLang != null
      ? localizedPath(
          `${PORTAL_PREFIX}/${PAGE_PORTAL_INVENTORY_CATEGORIES}`,
          language,
          defaultLang
        )
      : `/${PORTAL_PREFIX}/${PAGE_PORTAL_INVENTORY_CATEGORIES}`;

  const handleGoToCategories = () => {
    onClose();
    navigate(categoriesPortalPath);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="no-category-for-product-dialog"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(6px)",
            backgroundColor: alpha(theme.palette.common.black, 0.45)
          }
        },
        paper: {
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            maxHeight: "min(90vh, 640px)",
            display: "flex",
            flexDirection: "column",
            boxShadow: `0 24px 64px ${alpha(BRAND_COLOR, 0.22)}`,
            border: `1px solid ${alpha(BRAND_COLOR, 0.12)}`
          }
        }
      }}
    >
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: 2.5,
            pt: 2.5,
            pb: 2,
            textAlign: "center",
            background:
              ONBOARDING_DIALOG_HEADER_BG ??
              "linear-gradient(180deg, #edf6fc 0%, #e8f4fc 100%)",
            borderBottom: `1px solid ${ONBOARDING_DIALOG_HEADER_BORDER ?? "rgba(25, 118, 210, 0.2)"}`
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              mx: "auto",
              mb: 1.25,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(BRAND_COLOR, 0.1),
              border: `1px solid ${alpha(BRAND_COLOR, 0.14)}`,
              color: BRAND_COLOR
            }}
          >
            <CategoryOutlinedIcon sx={{ fontSize: 17 }} />
          </Box>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="text.primary"
            sx={{
              letterSpacing: "-0.02em",
              lineHeight: 1.3,
              fontSize: "1.05rem"
            }}
          >
            {t(
              "no-category-for-product-title",
              "Create a category before adding products"
            )}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.75, maxWidth: 360, mx: "auto", fontSize: "0.8125rem" }}
          >
            {t(
              "no-category-for-product-subtitle",
              "Categories organize your catalog — add one in minutes."
            )}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: 2.5,
            py: 2,
            bgcolor: "background.paper"
          }}
        >
          <Stack spacing={1.25}>
            {HIGHLIGHTS.map(
              (
                { Icon, titleKey, titleFallback, descKey, descFallback },
                index
              ) => (
                <Box
                  key={titleKey}
                  component={motion.div}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * (index + 1), duration: 0.3 }}
                  sx={{
                    display: "flex",
                    gap: 1.25,
                    alignItems: "flex-start",
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: alpha(BRAND_COLOR, 0.04),
                    border: `1px solid ${alpha(BRAND_COLOR, 0.08)}`
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      borderRadius: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(BRAND_COLOR, 0.1),
                      color: BRAND_COLOR
                    }}
                  >
                    <Icon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ fontSize: "0.8125rem", lineHeight: 1.35 }}
                    >
                      {t(titleKey, titleFallback)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.25, fontSize: "0.75rem", lineHeight: 1.4 }}
                    >
                      {t(descKey, descFallback)}
                    </Typography>
                  </Box>
                </Box>
              )
            )}
          </Stack>

          <Box
            sx={{
              mt: 1.5,
              p: 1.25,
              borderRadius: 2,
              bgcolor: GUIDANCE_CALLOUT_PANEL_BG ?? "#ffffff",
              border: `1px solid ${GUIDANCE_CALLOUT_PANEL_BORDER ?? "#e8f4fc"}`
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.75rem", lineHeight: 1.45 }}
            >
              {t(
                "no-category-for-product-runbook-prefix",
                "Need help getting started?"
              )}{" "}
              <MuiLink
                href={addCategorySopPath}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ color: BRAND_COLOR, fontWeight: 600 }}
                data-testid="no-category-for-product-runbook-link"
              >
                {t(
                  "no-category-for-product-runbook-link",
                  "Read the step-by-step guide to add a category"
                )}
              </MuiLink>
              {t("no-category-for-product-runbook-mid", " · ")}{" "}
              <MuiLink
                href={categoriesLearnMorePath}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ color: BRAND_COLOR, fontWeight: 600 }}
                data-testid="no-category-for-product-learn-more-link"
              >
                {t(
                  "no-category-for-product-learn-more-link",
                  "Learn how categories and subcategories work"
                )}
              </MuiLink>
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            px: 2.5,
            pb: 2,
            pt: 1.5,
            bgcolor: "background.paper",
            borderTop: `1px solid ${alpha(BRAND_COLOR, 0.08)}`
          }}
        >
          <Stack direction="row" spacing={1}>
            <Button
              size="medium"
              onClick={handleGoToCategories}
              variant="contained"
              data-testid="no-category-for-product-go-categories"
              sx={{
                flex: 1,
                py: 0.65,
                backgroundColor: BRAND_COLOR,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8125rem",
                boxShadow: `0 2px 8px ${alpha(BRAND_COLOR, 0.28)}`,
                "&:hover": {
                  backgroundColor: BRAND_HOVER,
                  boxShadow: `0 3px 12px ${alpha(BRAND_COLOR, 0.32)}`
                }
              }}
            >
              {t("no-category-for-product-go-categories", "Go to categories")}
            </Button>
            <Button
              size="medium"
              onClick={onClose}
              variant="outlined"
              data-testid="no-category-for-product-not-now"
              sx={{
                flex: 1,
                py: 0.65,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8125rem",
                borderColor: alpha(BRAND_COLOR, 0.35),
                color: BRAND_COLOR,
                "&:hover": {
                  borderColor: BRAND_COLOR,
                  bgcolor: alpha(BRAND_COLOR, 0.04)
                }
              }}
            >
              {t("no-category-for-product-not-now", "Not now")}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
};

export default NoCategoryForProductDialog;
